import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { todayISO } from "../lib/format";
import { toast } from "sonner";
import { Trash2, ArrowLeft, Wallet, ChevronDown, ChevronUp } from "lucide-react";
import { useAccounts } from "../lib/accounts";

const CATEGORIES = ["Food", "Groceries", "Transport", "Shopping", "Entertainment", "Bills", "Subscriptions", "Gifts", "Income", "Other"];

export default function TransactionForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [txn, setTxn] = useState(null);
  // "More details" collapsed by default for NEW transactions. Editing keeps it
  // open so power users can see everything at once. Preserves the smart-default
  // simplification (Amount + Payee are the only required fields in view).
  const [showMore, setShowMore] = useState(false);
  const { accounts, selectedId } = useAccounts();
  const [accountId, setAccountId] = useState("");

  const [form, setForm] = useState({
    date: params.get("date") || todayISO(),
    payee: params.get("payee") || "",
    amount: params.get("amount") || "",
    type: params.get("type") || "withdrawal",
    category: params.get("category") || "Other",
    memo: "",
    receipt_image: params.get("hasReceipt") === "1" ? sessionStorage.getItem("pending_receipt") || null : null,
    cleared: false,
  });

  useEffect(() => {
    if (id) {
      (async () => {
        const { data } = await api.get(`/transactions/${id}`);
        setTxn(data);
        setForm({
          date: data.date,
          payee: data.payee,
          amount: String(data.amount),
          type: data.type,
          category: data.category || "Other",
          memo: data.memo || "",
          receipt_image: data.receipt_image || null,
          cleared: data.cleared,
        });
        // Editing: lock to the transaction's own account
        setAccountId(data.account_id || "");
        // Editing an existing txn → open the details panel so nothing is
        // hidden from users trying to correct a bad row.
        setShowMore(true);
      })();
    }
  }, [id]);

  // For NEW transactions, default the target account to whatever the switcher has selected
  useEffect(() => {
    if (!id && !accountId && (selectedId || accounts[0]?.id)) {
      setAccountId(selectedId || accounts[0].id);
    }
  }, [id, accountId, selectedId, accounts]);

  const currentAccount = accounts.find((a) => a.id === accountId);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        date: form.date,
        payee: form.payee,
        amount: parseFloat(form.amount),
        type: form.type,
        category: form.category,
        memo: form.memo,
        cleared: form.cleared,
        account_id: accountId || undefined,
      };
      if (!id) payload.receipt_image = form.receipt_image;

      await saveTxn(payload);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
      setBusy(false);
    }
  };

  const saveTxn = async (payload) => {
    if (id) {
      await api.put(`/transactions/${id}`, payload);
      toast.success("Transaction updated");
    } else {
      await api.post("/transactions", payload);
      sessionStorage.removeItem("pending_receipt");
      toast.success("Transaction added");
    }
    nav("/register");
  };

  const remove = async () => {
    if (!id) return;
    if (!window.confirm("Delete this transaction?")) return;
    // Cache the payload so the Undo action can re-POST it. Server generates a
    // new id, but from the user's POV the row is back with all fields intact.
    const restorePayload = {
      date: form.date,
      payee: form.payee,
      amount: parseFloat(form.amount) || 0,
      type: form.type,
      category: form.category,
      memo: form.memo || "",
      receipt_image: form.receipt_image || null,
      cleared: !!form.cleared,
      account_id: accountId || null,
    };
    await api.delete(`/transactions/${id}`);
    nav("/register");
    toast("Transaction deleted", {
      duration: 6000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            await api.post("/transactions", restorePayload);
            toast.success(`Restored ${restorePayload.payee || "transaction"}`);
          } catch (e) {
            toast.error("Could not undo");
          }
        },
      },
    });
  };

  return (
    <div className="space-y-4" data-testid="txn-form-page">
      <div className="flex items-center justify-between">
        <Link to="/register" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">{id ? "Edit" : "New"}</h1>
        {id ? (
          <button
            data-testid="delete-txn-btn"
            onClick={remove}
            className="nb-btn nb-btn-danger !py-2 !px-3"
          >
            <Trash2 size={16} strokeWidth={3} />
          </button>
        ) : (
          <div className="w-10" />
        )}
      </div>

      <form onSubmit={submit} className="nb-card p-5 space-y-4" data-testid="txn-form">
        {accounts.length > 0 && (
          <div
            className="sticky -top-1 z-30 -mx-5 -mt-5 px-5 pt-4 pb-3 border-b-2 border-[#1a1a1a]"
            style={{ background: currentAccount?.color || "#fff" }}
            data-testid="txn-account-picker"
          >
            <div className="flex items-center gap-1.5 mb-2">
              <Wallet size={14} strokeWidth={3} />
              <div className="label !mb-0">
                {id ? "Bank account (edit to move)" : "Save this transaction to"}
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {accounts.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  data-testid={`txn-account-${a.name}`}
                  onClick={() => setAccountId(a.id)}
                  className={`chip whitespace-nowrap flex items-center gap-1.5 ${accountId === a.id ? "active" : ""}`}
                  style={accountId === a.id ? { background: a.color, borderColor: "#1a1a1a" } : {}}
                >
                  <Wallet size={12} strokeWidth={3} />
                  {a.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="type-withdrawal"
            onClick={() => setForm({ ...form, type: "withdrawal" })}
            className={`chip !py-3 !text-base ${form.type === "withdrawal" ? "active" : ""}`}
          >
            Spend
          </button>
          <button
            type="button"
            data-testid="type-deposit"
            onClick={() => setForm({ ...form, type: "deposit" })}
            className={`chip !py-3 !text-base ${
              form.type === "deposit" ? "active !bg-[#b5e4ca]" : ""
            }`}
          >
            Deposit
          </button>
        </div>

        <div>
          <label className="label">Amount</label>
          <input
            data-testid="txn-amount"
            type="number"
            step="0.01"
            required
            className="nb-input text-2xl font-display font-black"
            value={form.amount}
            onChange={set("amount")}
            placeholder="0.00"
          />
        </div>

        <div>
          <label className="label">Who?</label>
          <input
            data-testid="txn-payee"
            required
            className="nb-input"
            value={form.payee}
            onChange={set("payee")}
            placeholder="e.g. Chipotle"
          />
        </div>

        {/* More details — collapsed by default. Auto-defaults for new txns:
            today's date, category "Other" (backend will replace with the
            learned category if it knows this payee), no memo. */}
        {!showMore ? (
          <button
            type="button"
            data-testid="txn-more-toggle"
            onClick={() => setShowMore(true)}
            className="w-full flex items-center justify-center gap-1 py-2 text-xs font-bold text-[#4a4a4a] hover:text-[#1a1a1a] transition"
          >
            <ChevronDown size={14} strokeWidth={3} /> More details
          </button>
        ) : (
          <>
            <button
              type="button"
              data-testid="txn-less-toggle"
              onClick={() => setShowMore(false)}
              className="w-full flex items-center justify-center gap-1 py-1 text-xs font-bold text-[#4a4a4a] hover:text-[#1a1a1a] transition"
            >
              <ChevronUp size={14} strokeWidth={3} /> Hide details
            </button>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Date</label>
                <input
                  data-testid="txn-date"
                  type="date"
                  required
                  className="nb-input"
                  value={form.date}
                  onChange={set("date")}
                />
              </div>
              <div>
                <label className="label">Category</label>
                <select
                  data-testid="txn-category"
                  className="nb-select"
                  value={form.category}
                  onChange={set("category")}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="label">Note (optional)</label>
              <input
                data-testid="txn-memo"
                className="nb-input"
                value={form.memo}
                onChange={set("memo")}
                placeholder="Anything to remember"
              />
            </div>

            <label className="flex items-center gap-3 py-1">
              <input
                data-testid="txn-cleared"
                type="checkbox"
                checked={form.cleared}
                onChange={(e) => setForm({ ...form, cleared: e.target.checked })}
                className="w-5 h-5 border-2 border-[#1a1a1a] rounded"
              />
              <span className="font-bold">Already confirmed by bank</span>
            </label>
          </>
        )}

        {form.receipt_image && (
          <div>
            <label className="label">Receipt</label>
            <img
              data-testid="txn-receipt-img"
              src={
                form.receipt_image.startsWith("data:")
                  ? form.receipt_image
                  : `data:image/jpeg;base64,${form.receipt_image}`
              }
              alt="receipt"
              className="w-full max-h-72 object-contain border-2 border-[#1a1a1a] rounded-xl bg-white"
            />
          </div>
        )}

        <button
          type="submit"
          data-testid="save-txn-btn"
          disabled={busy}
          className="nb-btn nb-btn-dark w-full !py-3"
        >
          {busy ? "Saving…" : id ? "Save changes" : "Add transaction"}
        </button>
      </form>
    </div>
  );
}
